<template>
  <div style="width: 100%; height: 300px;">
    <v-chart
      ref="chartRef"
      class="chart-container"
      :option="chartOptions"
      autoresize
    />
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import VChart from 'vue-echarts'
import { markLineFor } from '@/zones/zoneMarkers'
import { useI18n } from 'vue-i18n'
import { currentLanguage } from '@/i18n'
import { formatNumber } from '@/i18n/format'
import { use } from 'echarts/core'
import { MarkLineComponent } from 'echarts/components'

use([MarkLineComponent])

const props = defineProps({
  chartData: Object, // expected shape: { "2025-05-04": { "Entrance 1": {...} }, ... }
  zoneName: String,
  timeSpan: String,
  // Optional weather overlay — OFF by default (production renders identically until
  // fed data). When on, `weatherByDate` maps each date key → daytime temperature (°C);
  // we draw it as a second, cold→hot colour-graded line on a right-hand axis so the
  // count series can be read against the weather. Demo passes it; prod later wires
  // scan_company_weather_hourly. Missing dates simply get no temperature point.
  showWeather: { type: Boolean, default: false },
  weatherByDate: { type: Object, default: null },
  // "Something changed at this spot" days (GET /zone_markers/): a dashed line each.
  markers: { type: Array, default: () => [] },
})

const chartRef = ref(null)

const { t } = useI18n()
// Dutch numbers (1.234 · 12,5) for tooltips and the value axis; English keeps
// ECharts' own formatting, so these are only set when the portal isn't English.
const nlNum = v => formatNumber(v, { decimals: Math.min(2, (String(v).split('.')[1] || '').length) })

const chartOptions = computed(() => {
  const dates = []
  const counts = []

  if (props.chartData && props.zoneName) {
    for (const [date, zoneData] of Object.entries(props.chartData)) {
      if (zoneData[props.zoneName]) {
        dates.push(date)
        counts.push(zoneData[props.zoneName].zone_in_count ?? 0)
      }
    }
  }

  const max = Math.max(...counts, 10)

  const showWx = !!(props.showWeather && props.weatherByDate)
  const temps = showWx ? dates.map(d => props.weatherByDate[d] ?? null) : []
  const tVals = temps.filter(t => t != null)
  const tMin = tVals.length ? Math.min(...tVals) : 0
  const tMax = tVals.length ? Math.max(...tVals) : 30

  return {
    tooltip: {
      trigger: 'axis',
      valueFormatter: currentLanguage() === 'en' ? undefined : nlNum
    },
    visualMap: [
      {
        show: false,
        type: 'continuous',
        seriesIndex: 0,
        min: 0,
        max: max,
        inRange: {
          color: ['#42546160', '#425461']
        }
      },
      // Cold→hot grading for the temperature line (only present when overlaying).
      ...(showWx ? [{
        show: false,
        type: 'piecewise',
        seriesIndex: 1,
        dimension: 1,
        pieces: [
          { lte: 20, color: '#7fb0d8' },
          { gt: 20, lte: 23, color: '#9fd0cb' },
          { gt: 23, lte: 26, color: '#e6c877' },
          { gt: 26, color: '#e0a94a' }
        ]
      }] : [])
    ],
    grid: {
      containLabel: true,
      bottom: 0,
      right: showWx ? 46 : undefined
    },
    xAxis: {
      type: 'category',
      data: dates,
      axisLabel: { rotate: 45 }
    },
    yAxis: [
      {
        type: 'value',
        name: t('charts.line.visitorsAxis', { span: props.timeSpan }),
        nameTextStyle: { align: 'left' },
        ...(currentLanguage() === 'en' ? {} : { axisLabel: { formatter: nlNum } })
      },
      ...(showWx ? [{
        type: 'value',
        name: '°C',
        position: 'right',
        min: Math.floor(tMin - 2),
        max: Math.ceil(tMax + 2),
        axisLabel: { formatter: '{value}°', color: '#b0863a' },
        nameTextStyle: { color: '#b0863a' },
        splitLine: { show: false }
      }] : [])
    ],
    series: [
      {
        name: t('charts.line.zoneInCount'),
        data: counts,
        markLine: markLineFor(props.markers, dates),
        type: 'line',
        smooth: true,
        lineStyle: {
          width: 3
        }
      },
      ...(showWx ? [{
        name: t('charts.line.temperature'),
        // encode dim 1 = temp so the piecewise visualMap can colour by value.
        data: temps.map((t, i) => [i, t]),
        type: 'line',
        yAxisIndex: 1,
        smooth: true,
        symbol: 'none',
        connectNulls: true,
        z: 3,
        lineStyle: { width: 2, type: 'dashed' },
        tooltip: { valueFormatter: v => {
          const c = Array.isArray(v) ? v[1] : v
          return currentLanguage() === 'en' ? `${c}°C` : `${nlNum(c)}°C`
        } }
      }] : [])
    ]
  }
})

onMounted(() => {
  setTimeout(() => {
    chartRef.value?.resize()
  }, 0)
})
</script>

<style scoped>
.chart-container {
  width: 100%;
  height: 100%;
}
</style>